import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, Area, Legend
} from 'recharts';
import { TrendingUp, Flame, Droplets, Activity, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { progressApi } from '../../services/api';

const ProgressCharts = ({ language = 'es' }) => {
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChart, setActiveChart] = useState('workouts');

  const periods = [
    { value: '7d', label: language === 'en' ? '7 Days' : '7 Días' },
    { value: '30d', label: language === 'en' ? 'Monthly' : 'Mensual' },
    { value: '6m', label: language === 'en' ? '6 Months' : '6 Meses' },
    { value: '1y', label: language === 'en' ? 'Annual' : 'Anual' }
  ];

  const chartTypes = [
    { 
      id: 'workouts', 
      label: language === 'en' ? 'Workouts' : 'Entrenamientos',
      icon: Activity,
      color: '#10b981'
    },
    { 
      id: 'calories', 
      label: language === 'en' ? 'Calories' : 'Calorías',
      icon: Flame,
      color: '#f97316'
    },
    { 
      id: 'hydration', 
      label: language === 'en' ? 'Hydration' : 'Hidratación',
      icon: Droplets,
      color: '#3b82f6'
    },
    { 
      id: 'combined', 
      label: language === 'en' ? 'Overview' : 'Resumen',
      icon: TrendingUp,
      color: '#8b5cf6'
    }
  ];

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await progressApi.getHistory(period);
      setData(result);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-3 shadow-xl">
        <p className="text-gray-400 text-sm mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-white text-sm">
              {entry.name}: {entry.value.toLocaleString()}
              {entry.dataKey === 'hydration' ? 'ml' : entry.dataKey === 'calories' ? ' kcal' : ''}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const chartData = data?.data || [];
  const summary = data?.summary || {};

  return (
    <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {language === 'en' ? 'Progress Charts' : 'Gráficos de Progreso'}
            </h2>
            <p className="text-gray-500 text-sm">
              {language === 'en' ? 'Track your journey' : 'Sigue tu progreso'}
            </p>
          </div>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2 flex-wrap">
          {periods.map((p) => (
            <Button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              variant={period === p.value ? 'default' : 'outline'}
              size="sm"
              className={period === p.value 
                ? 'bg-emerald-500 hover:bg-emerald-600' 
                : 'border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800'
              }
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SummaryCard
          icon={Activity}
          value={summary.total_workouts || 0}
          label={language === 'en' ? 'Workouts' : 'Entrenamientos'}
          subtext={`${summary.avg_workouts_per_day || 0}/día`}
          color="emerald"
        />
        <SummaryCard
          icon={Flame}
          value={`${(summary.total_calories || 0).toLocaleString()}`}
          label={language === 'en' ? 'Calories' : 'Calorías'}
          subtext={`${summary.avg_calories_per_day || 0} kcal/día`}
          color="orange"
        />
        <SummaryCard
          icon={Droplets}
          value={`${((summary.total_hydration || 0) / 1000).toFixed(1)}L`}
          label={language === 'en' ? 'Water' : 'Agua'}
          subtext={language === 'en' ? 'total' : 'total'}
          color="blue"
        />
        <SummaryCard
          icon={Calendar}
          value={summary.current_streak || 0}
          label={language === 'en' ? 'Streak' : 'Racha'}
          subtext={language === 'en' ? 'days' : 'días'}
          color="yellow"
        />
      </div>

      {/* Chart Type Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {chartTypes.map((chart) => (
          <button
            key={chart.id}
            onClick={() => setActiveChart(chart.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all ${
              activeChart === chart.id
                ? 'bg-gray-800 text-white'
                : 'text-gray-500 hover:text-white hover:bg-gray-800/50'
            }`}
          >
            <chart.icon className="w-4 h-4" style={{ color: chart.color }} />
            <span className="text-sm font-medium">{chart.label}</span>
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="h-72">
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{language === 'en' ? 'No data for this period' : 'Sin datos para este período'}</p>
              <p className="text-sm mt-1">
                {language === 'en' ? 'Start working out to see your progress!' : '¡Empieza a entrenar para ver tu progreso!'}
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {activeChart === 'workouts' && (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="label" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="workouts" 
                  name={language === 'en' ? 'Workouts' : 'Entrenamientos'}
                  fill="#10b981" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            )}

            {activeChart === 'calories' && (
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="label" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="calories" 
                  name={language === 'en' ? 'Calories' : 'Calorías'}
                  fill="#f97316" 
                  radius={[4, 4, 0, 0]}
                  fillOpacity={0.8}
                />
                <Line 
                  type="monotone" 
                  dataKey="calories" 
                  stroke="#fb923c" 
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            )}

            {activeChart === 'hydration' && (
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="label" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value / 1000}L`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="hydration" 
                  name={language === 'en' ? 'Water' : 'Agua'}
                  fill="#3b82f6" 
                  fillOpacity={0.3}
                  stroke="#3b82f6"
                  strokeWidth={2}
                />
              </ComposedChart>
            )}

            {activeChart === 'combined' && (
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="label" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  yAxisId="left"
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value) => <span className="text-gray-300 text-sm">{value}</span>}
                />
                <Bar 
                  yAxisId="left"
                  dataKey="workouts" 
                  name={language === 'en' ? 'Workouts' : 'Entrenamientos'}
                  fill="#10b981" 
                  radius={[4, 4, 0, 0]}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="calories" 
                  name={language === 'en' ? 'Calories' : 'Calorías'}
                  stroke="#f97316" 
                  strokeWidth={2}
                  dot={{ fill: '#f97316', strokeWidth: 0, r: 3 }}
                />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, value, label, subtext, color }) => {
  const colors = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    orange: 'bg-orange-500/20 text-orange-400',
    blue: 'bg-blue-500/20 text-blue-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    purple: 'bg-purple-500/20 text-purple-400'
  };

  return (
    <div className="bg-gray-800/50 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-6 h-6 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-3 h-3" />
        </div>
        <span className="text-lg font-bold text-white">{value}</span>
      </div>
      <p className="text-gray-400 text-xs">{label}</p>
      <p className="text-gray-600 text-xs">{subtext}</p>
    </div>
  );
};

export default ProgressCharts;
